import {
  Container,
  Flex,
  Tab,
  TabPanels,
  TabList,
  TabPanel,
  Tabs,
  Text,
  HStack,
  Center,
  Spinner,
  Alert,
  AlertIcon,
  useDisclosure,
} from '@chakra-ui/react';
import { useState, useContext } from 'react';
import Breadcrumbs from '../../../common/Breadcrumbs';
import { MeContext } from '../../../context/MeContext';
import { useGetMeQuery } from '../../../store/redux/api/accountApi';
import CancelTrialModal from './CancelTrialModal';
import { useBilling } from '../../../store/client/BillingContext';
import PlanAndSubscription from './PlanandSubscription';
import UsageAndLimits from './UsageAndLimits';
import SubscriptionComparisonModal from './SubscriptionComparisonModal';
import ResubscribeModal from './ResubscribeModal';
import BillingAndPayment from './BillingAndPayment';
import PendingCustomPlanBanner from './PendingCustomPlanBanner';
import CustomPlanOfferCard from './CustomPlanOfferCard';
import RequestCustomPlanModal from './RequestCustomPlanModal';
import PageHeadingWithTrialProgress from '../dashboard/PageHeadingWithTrialProgress';
import CardlessTrialConversionModal from './CardlessTrialConversionModal';
import { formatGbpFromMinorUnits } from '../../../utils/subscriptionUtils';

const TAB_KEYS = ['plan', 'usage', 'billing'];

const BillingPageContent = () => {
  const meCtx = useContext(MeContext);
  const cardlessTrialStartedAt = meCtx.state.cardlessTrialStartedAt;
  const { data: me } = useGetMeQuery();
  const entitlementsActive = Boolean(me?.entitlements_active);
  const [tabIndex, setTabIndex] = useState(0);
  const cancelTrialModal = useDisclosure();
  const subscriptionComparisonModal = useDisclosure();
  const resubscribeModal = useDisclosure();
  const requestCustomPlanModal = useDisclosure();
  const cardlessTrialConversionModal = useDisclosure();
  const [customPlanBillingInterval, setCustomPlanBillingInterval] =
    useState(null);

  const {
    subscription,
    paymentMethod,
    isLoading,
    refresh,
    customPlanRequest,
    trial,
  } = useBilling();

  // Legacy-billing orgs have org-scoped entitlements but no Stripe subscription
  // or cardless trial. There's no plan/payment to manage, so only the
  // "Usage & limits" tab is shown.
  const isLegacyEntitlementsOnly =
    entitlementsActive && !subscription && !cardlessTrialStartedAt;
  const tabKeys = isLegacyEntitlementsOnly ? ['usage'] : TAB_KEYS;
  const activeKey = tabKeys[tabIndex] ?? tabKeys[0];

  const breadcrumbLinks = [
    { linkName: 'subscription', linkUrl: '/subscription' },
    {
      linkName:
        activeKey === 'plan'
          ? 'plan & subscription'
          : activeKey === 'usage'
            ? 'usage & limits'
            : 'billing & payment',
      linkUrl:
        activeKey === 'plan'
          ? '/plan-and-subscription'
          : activeKey === 'usage'
            ? '/usage-and-limits'
            : '/billing-and-payment',
    },
  ];

  const formatPrice = (amount) =>
    formatGbpFromMinorUnits(amount, { wholePounds: true });

  const { daysRemainingInTrial, onTrial } = subscription || {
    daysRemainingInTrial: 0,
    onTrial: false,
  };

  const getStatusMessage = () => {
    if (subscription?.status === 'incomplete') {
      return {
        status: 'warning',
        message:
          "Your subscription payment couldn't be processed. Please update your payment method",
      };
    }
    if (subscription?.canResubscribe) {
      return {
        status: 'error',
        message:
          'Your subscription has been canceled. Please resubscribe to continue.',
        action: 'Resubscribe',
        buttonAction: () => subscriptionComparisonModal.onOpen(),
      };
    }
    return {
      status: 'success',
      message: null,
      action: 'Update',
      buttonAction: () => subscriptionComparisonModal.onOpen(),
    };
  };

  const statusInfo = getStatusMessage();

  if (isLoading) {
    return (
      <Center h="100vh">
        <Spinner />
      </Center>
    );
  }

  return (
    <Container display="flex" flexDir="column" flex="1">
      <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />
      <PageHeadingWithTrialProgress
        title="Subscription"
        titleProps={{ 'data-testid': 'subscription-heading' }}
      />

      <Tabs pt={2} w="100%" index={tabIndex} onChange={setTabIndex}>
        <TabList>
          {!isLegacyEntitlementsOnly && (
            <Tab cursor="pointer">
              <HStack alignItems="baseline">
                <Text textStyle="bodyMedium">Plan & subscription</Text>
              </HStack>
            </Tab>
          )}
          <Tab cursor="pointer">
            <HStack alignItems="baseline">
              <Text textStyle="bodyMedium">Usage & limits</Text>
            </HStack>
          </Tab>
          {subscription && (
            <Tab cursor="pointer">
              <HStack alignItems="baseline">
                <Text textStyle="bodyMedium">Billing & payment</Text>
              </HStack>
            </Tab>
          )}
        </TabList>
        {statusInfo.message && (
          <Alert status={statusInfo.status} borderRadius="5px" mt={6}>
            <AlertIcon />
            {statusInfo.message}
          </Alert>
        )}
        <TabPanels mt={4}>
          {!isLegacyEntitlementsOnly && (
            <TabPanel px="0">
              <Flex direction="column" gap={12}>
                <PendingCustomPlanBanner
                  customPlanRequest={customPlanRequest}
                />
                <CustomPlanOfferCard customPlanRequest={customPlanRequest} />
                <PlanAndSubscription
                  subscription={subscription}
                  trial={trial}
                  paymentMethod={paymentMethod}
                  isLoading={isLoading}
                  daysRemainingInTrial={daysRemainingInTrial}
                  onTrial={onTrial}
                  onOpenCancelTrialModal={cancelTrialModal.onOpen}
                  formatPrice={formatPrice}
                  onOpenSubscriptionComparisonModal={
                    subscriptionComparisonModal.onOpen
                  }
                  onOpenResubscribeModal={resubscribeModal.onOpen}
                  onOpenCardlessTrialConversionModal={
                    cardlessTrialConversionModal.onOpen
                  }
                  customPlanRequest={customPlanRequest}
                />
              </Flex>
            </TabPanel>
          )}
          <TabPanel px="0">
            <UsageAndLimits />
          </TabPanel>
          <TabPanel px="0">
            <BillingAndPayment
              subscription={subscription}
              paymentMethod={paymentMethod}
              onTrial={onTrial}
              formatPrice={formatPrice}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
      <CancelTrialModal
        isOpen={cancelTrialModal.isOpen}
        onClose={cancelTrialModal.onClose}
        onSuccess={() => {
          cancelTrialModal.onClose();
          refresh();
        }}
      />
      <SubscriptionComparisonModal
        isOpen={subscriptionComparisonModal.isOpen}
        customPlanRequest={customPlanRequest}
        onOpenRequestCustomPlan={(interval) => {
          setCustomPlanBillingInterval(interval);
          requestCustomPlanModal.onOpen();
        }}
        onClose={() => {
          subscriptionComparisonModal.onClose();
        }}
      />
      <ResubscribeModal
        isOpen={resubscribeModal.isOpen}
        handleClose={() => {
          resubscribeModal.onClose();
        }}
        onSuccess={async () => {
          resubscribeModal.onClose();
          if (refresh) {
            await refresh();
          }
        }}
        existingPaymentMethod={paymentMethod}
        subscription={subscription}
      />
      <CardlessTrialConversionModal
        isOpen={cardlessTrialConversionModal.isOpen}
        onClose={cardlessTrialConversionModal.onClose}
      />
      <RequestCustomPlanModal
        isOpen={requestCustomPlanModal.isOpen}
        onClose={() => {
          setCustomPlanBillingInterval(null);
          requestCustomPlanModal.onClose();
        }}
        onSuccess={async () => {
          subscriptionComparisonModal.onClose();
          if (refresh) {
            await refresh();
          }
        }}
        subscription={subscription}
        selectedInterval={
          customPlanBillingInterval ?? subscription?.plan?.interval ?? 'year'
        }
      />
    </Container>
  );
};

export default BillingPageContent;
